import React, { useCallback, useEffect, useState } from "react";
import PageHeader from "../components/shared/pageHeader/PageHeader";
import { FiRefreshCw, FiSave, FiEdit, FiPlus, FiTrash2 } from "react-icons/fi";
import { RxDragHandleDots2 } from "react-icons/rx";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import "react-toastify/dist/ReactToastify.css";
import "./CustomSwitchPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const createTempId = () => `tmp-${Date.now()}-${Math.random()}`;

const emptyForm = {
	title: "",
	url: "",
	from_module: false,
	source_module_data_id: "",
	url_locked: false,
};
const CustomSwitchPage = () => {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	const [showModal, setShowModal] = useState(false);
	const [modalMode, setModalMode] = useState(null);
	const [editTarget, setEditTarget] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [modulePages, setModulePages] = useState([]);
	const [modulePagesLoading, setModulePagesLoading] = useState(false);

	const fetchMenu = useCallback(async () => {
		setLoading(true);

		try {
			const resp = await axios.get(
				`${API_BASE}/api/frontend-menu/category-menu?lang=tr`,
			);

			const data = Array.isArray(resp.data) ? resp.data : [];

			const normalized = data
				.map((item, index) => ({
					...item,
					temp_id: item.temp_id || createTempId(),
					item_type: item.item_type || "page",
					title: item.title || "",
					url: item.url || "#",
					is_visible: !!item.is_visible,
					url_locked: !!item.url_locked,
					sort_order: Number(item.sort_order ?? index),
					children: Array.isArray(item.children)
						? item.children
								.map((child, childIndex) => ({
									...child,
									temp_id: child.temp_id || createTempId(),
									item_type: "page",
									title: child.title || "",
									url: child.url || "#",
									url_locked: !!child.url_locked,
									is_visible: !!child.is_visible,
									sort_order: Number(child.sort_order ?? childIndex),
									children: [],
								}))
								.sort(
									(a, b) =>
										Number(a.sort_order || 0) - Number(b.sort_order || 0),
								)
						: [],
				}))
				.sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));

			setItems(normalized);
		} catch (error) {
			console.error("Menü alınamadı:", error);
			toast.error("Menü yüklenirken hata oluştu.");
		}

		setLoading(false);
	}, []);

	const fetchModulePages = async () => {
		setModulePagesLoading(true);

		try {
			const response = await axios.get(
				`${API_BASE}/api/frontend-menu/module-pages`,
				{
					params: {
						lang: "tr",
						module_id: 70,
					},
				},
			);

			setModulePages(Array.isArray(response.data) ? response.data : []);
		} catch (error) {
			console.error("Modül sayfaları alınamadı:", error);
			toast.error("Modüldeki sayfalar alınamadı.");
		} finally {
			setModulePagesLoading(false);
		}
	};

	const handleFromModuleChange = async (checked) => {
		setForm((prev) => ({
			...prev,
			from_module: checked,
			source_module_data_id: "",
			url: checked ? "" : prev.url,
			url_locked: checked,
		}));

		if (checked && modulePages.length === 0) {
			await fetchModulePages();
		}
	};

	useEffect(() => {
		fetchMenu();
	}, [fetchMenu]);

	const reorder = (list, sourceIndex, destIndex) => {
		const result = Array.from(list);
		const [removed] = result.splice(sourceIndex, 1);
		result.splice(destIndex, 0, removed);

		return result.map((item, index) => ({
			...item,
			sort_order: index,
		}));
	};

	const handleDragEnd = (result) => {
		const { source, destination, type } = result;

		if (!destination) return;

		if (
			source.droppableId === destination.droppableId &&
			source.index === destination.index
		) {
			return;
		}

		if (type === "TOP_ITEM") {
			setItems((prev) => reorder(prev, source.index, destination.index));
			return;
		}

		if (type === "CHILD_ITEM") {
			const sourceParentKey = source.droppableId.replace("children-", "");
			const destParentKey = destination.droppableId.replace("children-", "");

			if (sourceParentKey !== destParentKey) {
				toast.warning(
					"Alt menüyü başka üst menüye taşımayı şimdilik kapattık.",
				);
				return;
			}

			setItems((prev) =>
				prev.map((item) => {
					const key = String(item.id || item.temp_id);

					if (key !== sourceParentKey) return item;

					return {
						...item,
						children: reorder(
							item.children || [],
							source.index,
							destination.index,
						),
					};
				}),
			);
		}
	};

	const openAddTopCategory = () => {
		setModalMode("add_category");
		setEditTarget(null);

		setForm({
			...emptyForm,
			url: "#",
		});

		setShowModal(true);
	};

	const openAddTopPage = () => {
		setModalMode("add_page");
		setEditTarget(null);
		setForm({ ...emptyForm });
		setShowModal(true);
	};

	const openAddChildPage = (parentIndex) => {
		setModalMode("add_child");
		setEditTarget({ parentIndex });
		setForm({ ...emptyForm });
		setShowModal(true);
	};

	const openEditTop = (index) => {
		const item = items[index];

		setModalMode("edit_top");
		setEditTarget({ index });

		setForm({
			title: item.title || "",
			url: item.url || "#",
			from_module: false,
			source_module_data_id: "",
			url_locked: !!item.url_locked,
		});

		setShowModal(true);
	};

	const openEditChild = (parentIndex, childIndex) => {
		const child = items[parentIndex].children[childIndex];

		setModalMode("edit_child");
		setEditTarget({ parentIndex, childIndex });

		setForm({
			title: child.title || "",
			url: child.url || "#",
			from_module: false,
			source_module_data_id: "",
			url_locked: !!child.url_locked,
		});

		setShowModal(true);
	};

	const closeModal = () => {
		setShowModal(false);
		setModalMode(null);
		setEditTarget(null);
		setForm(emptyForm);
	};

	const handleModalSave = () => {
		if (!form.title.trim()) {
			toast.warning("Başlık boş olamaz.");
			return;
		}

		if (
			["add_page", "add_child", "edit_child"].includes(modalMode) &&
			!form.url.trim()
		) {
			toast.warning("Bağlantı boş olamaz.");
			return;
		}
		if (
			["add_page", "add_child"].includes(modalMode) &&
			form.from_module &&
			!form.source_module_data_id
		) {
			toast.warning("Modülden bir sayfa seçmelisiniz.");
			return;
		}

		setItems((prev) => {
			const updated = [...prev];

			if (modalMode === "add_category") {
				updated.push({
					id: null,
					temp_id: createTempId(),
					item_type: "category",
					title: form.title.trim(),
					url: "#",
					is_visible: true,
					sort_order: updated.length,
					module_data_id: null,
					module_category_id: null,
					parent_category_id: null,
					children: [],
				});
			}

			if (modalMode === "add_page") {
				updated.push({
					id: null,
					temp_id: createTempId(),
					item_type: "page",
					title: form.title.trim(),
					url_locked: !!form.url_locked,
					from_module: !!form.from_module,
					source_module_data_id: form.from_module
						? Number(form.source_module_data_id)
						: null,
					url: form.url.trim(),
					is_visible: true,
					sort_order: updated.length,
					module_data_id: null,
					module_category_id: null,
					parent_category_id: null,
					children: [],
				});
			}

			if (modalMode === "add_child") {
				const parentIndex = editTarget.parentIndex;
				const parent = updated[parentIndex];
				const children = Array.isArray(parent.children)
					? [...parent.children]
					: [];

				children.push({
					id: null,
					temp_id: createTempId(),
					item_type: "page",
					title: form.title.trim(),
					url_locked: !!form.url_locked,
					from_module: !!form.from_module,
					source_module_data_id: form.from_module
						? Number(form.source_module_data_id)
						: null,
					url: form.url.trim(),
					is_visible: true,
					sort_order: children.length,
					module_data_id: null,
					module_category_id: null,
					parent_category_id: parent.id || null,
					children: [],
				});

				updated[parentIndex] = {
					...parent,
					children,
				};
			}

			if (modalMode === "edit_top") {
				const index = editTarget.index;
				const item = updated[index];

				updated[index] = {
					...item,
					title: form.title.trim(),
					url: item.item_type === "category" ? "#" : form.url.trim(),
				};
			}

			if (modalMode === "edit_child") {
				const { parentIndex, childIndex } = editTarget;
				const parent = updated[parentIndex];
				const children = [...(parent.children || [])];

				children[childIndex] = {
					...children[childIndex],
					title: form.title.trim(),
					url: form.url.trim(),
				};

				updated[parentIndex] = {
					...parent,
					children,
				};
			}

			return updated.map((item, index) => ({
				...item,
				sort_order: index,
			}));
		});

		closeModal();
	};

	const handleModulePageSelect = (moduleDataId) => {
		const selectedPage = modulePages.find(
			(page) => String(page.id) === String(moduleDataId),
		);

		if (!selectedPage) {
			setForm((prev) => ({
				...prev,
				source_module_data_id: "",
				title: "",
				url: "",
				url_locked: false,
			}));

			return;
		}

		setForm((prev) => ({
			...prev,
			source_module_data_id: String(selectedPage.id),
			title: selectedPage.title,
			url: selectedPage.url,
			url_locked: true,
		}));
	};

	const toggleTop = (index) => {
		setItems((prev) => {
			const updated = [...prev];

			updated[index] = {
				...updated[index],
				is_visible: !updated[index].is_visible,
			};

			return updated;
		});
	};

	const toggleChild = (parentIndex, childIndex) => {
		setItems((prev) => {
			const updated = [...prev];
			const parent = updated[parentIndex];
			const children = [...(parent.children || [])];

			children[childIndex] = {
				...children[childIndex],
				is_visible: !children[childIndex].is_visible,
			};

			updated[parentIndex] = {
				...parent,
				children,
			};

			return updated;
		});
	};

	const deleteTop = async (index) => {
		const item = items[index];

		if (!window.confirm("Bu menü silinsin mi?")) return;

		try {
			if (item.id) {
				await axios.delete(
					`${API_BASE}/api/frontend-menu/menu-visibility/${item.id}`,
				);
			}

			setItems((prev) => prev.filter((_, i) => i !== index));
			toast.success("Menü silindi.");
		} catch (error) {
			console.error("Menü silinemedi:", error);
			toast.error("Menü silinirken hata oluştu.");
		}
	};

	const deleteChild = async (parentIndex, childIndex) => {
		const child = items[parentIndex].children[childIndex];

		if (!window.confirm("Alt menü silinsin mi?")) return;

		try {
			if (child.id) {
				await axios.delete(
					`${API_BASE}/api/frontend-menu/menu-visibility/${child.id}`,
				);
			}

			setItems((prev) => {
				const updated = [...prev];
				const parent = updated[parentIndex];

				updated[parentIndex] = {
					...parent,
					children: parent.children.filter((_, i) => i !== childIndex),
				};

				return updated;
			});

			toast.success("Alt menü silindi.");
		} catch (error) {
			console.error("Alt menü silinemedi:", error);
			toast.error("Alt menü silinirken hata oluştu.");
		}
	};

	const handleSave = async () => {
		setSaving(true);

		try {
			const payload = {
				lang: "tr",
				items: items.map((item, index) => ({
					...item,
					sort_order: index,
					children: (item.children || []).map((child, childIndex) => ({
						...child,
						sort_order: childIndex,
					})),
				})),
			};

			const resp = await axios.post(
				`${API_BASE}/api/frontend-menu/category-menu`,
				payload,
			);

			toast.success(resp.data?.message || "Menü başarıyla kaydedildi.");
			await fetchMenu();
		} catch (error) {
			console.error("Menü kaydedilemedi:", error);
			toast.error("Menü kaydedilirken hata oluştu.");
		}

		setSaving(false);
	};

	const modalTitle =
		modalMode === "add_category"
			? "Üst Menü Ekle"
			: modalMode === "add_page"
				? "Normal Sayfa Ekle"
				: modalMode === "add_child"
					? "Alt Menü Ekle"
					: "Menü Düzenle";

	return (
		<>
			<PageHeader />

			<div className="main-content">
				<div className="row">
					<div className="col-12 page-content p-4">
						<ToastContainer />

						<div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
							<div>
								<h5 className="mb-1">Frontend Menü Yönetimi</h5>
								<p className="text-muted mb-0">
									Üst menü, alt menü veya normal sayfa ekleyebilirsiniz.
								</p>
							</div>

							<div className="d-flex gap-2 flex-wrap">
								<button
									className="btn btn-outline-primary btn-sm d-flex align-items-center"
									onClick={openAddTopCategory}
								>
									<FiPlus className="me-1" />
									Üst Menü Ekle
								</button>

								<button
									className="btn btn-outline-success btn-sm d-flex align-items-center"
									onClick={openAddTopPage}
								>
									<FiPlus className="me-1" />
									Normal Sayfa Ekle
								</button>

								<button
									className="btn btn-outline-secondary btn-sm d-flex align-items-center"
									onClick={fetchMenu}
									disabled={loading}
								>
									<FiRefreshCw
										className={`me-1 ${loading ? "spin-animation" : ""}`}
									/>
									Yenile
								</button>

								<button
									className="btn btn-primary btn-sm d-flex align-items-center"
									onClick={handleSave}
									disabled={saving}
								>
									<FiSave className="me-1" />
									{saving ? "Kaydediliyor..." : "Kaydet"}
								</button>
							</div>
						</div>

						<div className="card function-table">
							<div className="card-body p-0">
								<div className="panel-body p-4">
									{loading && (
										<p className="text-center p-3">Menü yükleniyor...</p>
									)}

									{!loading && items.length === 0 && (
										<p className="text-center text-muted p-3">
											Henüz menü eklenmemiş.
										</p>
									)}

									{!loading && items.length > 0 && (
										<DragDropContext onDragEnd={handleDragEnd}>
											<Droppable droppableId="top-items" type="TOP_ITEM">
												{(provided) => (
													<div
														ref={provided.innerRef}
														{...provided.droppableProps}
													>
														{items.map((item, index) => {
															const itemKey = String(item.id || item.temp_id);
															const isCategory = item.item_type === "category";

															return (
																<Draggable
																	key={`top-${itemKey}`}
																	draggableId={`top-${itemKey}`}
																	index={index}
																>
																	{(provided, snapshot) => (
																		<div
																			ref={provided.innerRef}
																			{...provided.draggableProps}
																			className="mb-3 border rounded bg-white"
																			style={{
																				...provided.draggableProps.style,
																				boxShadow: snapshot.isDragging
																					? "0 10px 24px rgba(15,23,42,0.15)"
																					: "none",
																			}}
																		>
																			<div className="d-flex align-items-center justify-content-between p-3 bg-light border-bottom">
																				<div className="d-flex align-items-center gap-2">
																					<span
																						{...provided.dragHandleProps}
																						style={{ cursor: "grab" }}
																					>
																						<RxDragHandleDots2
																							size={22}
																							className="text-muted"
																						/>
																					</span>

																					<strong>{item.title}</strong>

																					<span
																						className={`badge ${
																							isCategory
																								? "bg-primary"
																								: "bg-success"
																						}`}
																					>
																						{isCategory
																							? "Üst Menü"
																							: "Normal Sayfa"}
																					</span>

																					{isCategory && (
																						<span className="badge bg-secondary">
																							{(item.children || []).length} alt
																							menü
																						</span>
																					)}

																					{!isCategory && (
																						<code className="text-muted small">
																							{item.url}
																						</code>
																					)}
																				</div>

																				<div className="d-flex align-items-center gap-2">
																					{isCategory && (
																						<button
																							className="btn btn-sm btn-outline-primary"
																							onClick={() =>
																								openAddChildPage(index)
																							}
																						>
																							<FiPlus className="me-1" />
																							Alt Menü
																						</button>
																					)}

																					<button
																						className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
																						title="Düzenle"
																						onClick={() => openEditTop(index)}
																					>
																						<FiEdit />
																					</button>

																					<button
																						className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
																						title="Sil"
																						onClick={() => deleteTop(index)}
																					>
																						<FiTrash2 />
																					</button>

																					<label className="custom-switch-lg mb-0">
																						<input
																							type="checkbox"
																							checked={!!item.is_visible}
																							onChange={() => toggleTop(index)}
																						/>
																						<span className="custom-slider"></span>
																					</label>
																				</div>
																			</div>

																			{isCategory && (
																				<div className="p-3">
																					<Droppable
																						droppableId={`children-${itemKey}`}
																						type="CHILD_ITEM"
																					>
																						{(provided) => (
																							<div
																								ref={provided.innerRef}
																								{...provided.droppableProps}
																							>
																								{(item.children || [])
																									.length === 0 && (
																									<p className="text-muted mb-0">
																										Bu üst menünün altında alt
																										menü yok.
																									</p>
																								)}

																								{(item.children || []).map(
																									(child, childIndex) => {
																										const childKey = String(
																											child.id || child.temp_id,
																										);

																										return (
																											<Draggable
																												key={`child-${itemKey}-${childKey}`}
																												draggableId={`child-${itemKey}-${childKey}`}
																												index={childIndex}
																											>
																												{(
																													provided,
																													snapshot,
																												) => (
																													<div
																														ref={
																															provided.innerRef
																														}
																														{...provided.draggableProps}
																														className="d-flex align-items-center justify-content-between border rounded p-2 mb-2"
																														style={{
																															...provided
																																.draggableProps
																																.style,
																															background:
																																snapshot.isDragging
																																	? "#f0f4ff"
																																	: "#fff",
																														}}
																													>
																														<div className="d-flex align-items-center gap-2">
																															<span
																																{...provided.dragHandleProps}
																																style={{
																																	cursor:
																																		"grab",
																																}}
																															>
																																<RxDragHandleDots2
																																	size={20}
																																	className="text-muted"
																																/>
																															</span>

																															<div>
																																<div className="fw-semibold">
																																	{child.title}
																																</div>
																																<code className="text-muted small">
																																	{child.url}
																																</code>
																															</div>
																														</div>

																														<div className="d-flex align-items-center gap-2">
																															<button
																																className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
																																title="Alt menü düzenle"
																																onClick={() =>
																																	openEditChild(
																																		index,
																																		childIndex,
																																	)
																																}
																															>
																																<FiEdit />
																															</button>

																															<button
																																className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
																																title="Alt menü sil"
																																onClick={() =>
																																	deleteChild(
																																		index,
																																		childIndex,
																																	)
																																}
																															>
																																<FiTrash2 />
																															</button>

																															<label className="custom-switch-lg mb-0">
																																<input
																																	type="checkbox"
																																	checked={
																																		!!child.is_visible
																																	}
																																	onChange={() =>
																																		toggleChild(
																																			index,
																																			childIndex,
																																		)
																																	}
																																/>
																																<span className="custom-slider"></span>
																															</label>
																														</div>
																													</div>
																												)}
																											</Draggable>
																										);
																									},
																								)}

																								{provided.placeholder}
																							</div>
																						)}
																					</Droppable>
																				</div>
																			)}
																		</div>
																	)}
																</Draggable>
															);
														})}

														{provided.placeholder}
													</div>
												)}
											</Droppable>
										</DragDropContext>
									)}
								</div>
							</div>
						</div>

						<div className="d-flex justify-content-center mt-3">
							<button
								className="btn btn-primary d-flex align-items-center shadow-sm"
								onClick={handleSave}
								disabled={saving}
							>
								<FiSave className="me-2" />
								{saving ? "Kaydediliyor..." : "Kaydet"}
							</button>
						</div>
					</div>
				</div>
			</div>

			{showModal && (
				<div
					className="modal fade show"
					style={{
						display: "block",
						backgroundColor: "rgba(0,0,0,0.5)",
					}}
					tabIndex="-1"
				>
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">{modalTitle}</h5>

								<button
									type="button"
									className="btn-close"
									onClick={closeModal}
								></button>
							</div>

							<div className="modal-body">
								{["add_page", "add_child"].includes(modalMode) && (
									<div className="mb-3">
										<div className="form-check">
											<input
												id="from-module-checkbox"
												type="checkbox"
												className="form-check-input"
												checked={!!form.from_module}
												onChange={(e) =>
													handleFromModuleChange(e.target.checked)
												}
											/>

											<label
												htmlFor="from-module-checkbox"
												className="form-check-label"
											>
												Modülden Seç
											</label>
										</div>
									</div>
								)}

								{["add_page", "add_child"].includes(modalMode) &&
									form.from_module && (
										<div className="mb-3">
											<label className="form-label">Sayfa Seç</label>

											<select
												className="form-select"
												value={form.source_module_data_id}
												disabled={modulePagesLoading}
												onChange={(e) => handleModulePageSelect(e.target.value)}
											>
												<option value="">
													{modulePagesLoading
														? "Sayfalar yükleniyor..."
														: "Sayfa seçiniz"}
												</option>

												{modulePages.map((page) => (
													<option key={page.id} value={page.id}>
														{page.title}
													</option>
												))}
											</select>
										</div>
									)}
								<div className="mb-3">
									<label className="form-label">Başlık</label>
									<input
										type="text"
										className="form-control"
										value={form.title}
										onChange={(e) =>
											setForm((prev) => ({
												...prev,
												title: e.target.value,
											}))
										}
									/>
								</div>

								{modalMode !== "add_category" &&
									!(
										modalMode === "edit_top" &&
										items[editTarget?.index]?.item_type === "category"
									) && (
										<div className="mb-3">
											<label className="form-label">Bağlantı</label>

											<input
												type="text"
												className="form-control"
												placeholder="/ornek-sayfa"
												value={form.url}
												readOnly={form.url_locked}
												onChange={(e) =>
													setForm((prev) => ({
														...prev,
														url: e.target.value,
													}))
												}
											/>

											{form.url_locked && (
												<small className="text-muted">
													Bağlantı seçilen modül sayfasından otomatik
													oluşturulduğu için değiştirilemez.
												</small>
											)}
										</div>
									)}
							</div>

							<div className="modal-footer">
								<button className="btn btn-secondary" onClick={closeModal}>
									İptal
								</button>

								<button className="btn btn-primary" onClick={handleModalSave}>
									Kaydet
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default CustomSwitchPage;
